import * as React from "react";
import { AppBundle, Application, ApplicationsService } from "gen/clients";
import { DataTable } from "components/DataTable/DataTable";
import { DataTableColumn, DataTableRowAction } from "components/DataTable/types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { useQuery } from "react-query";

interface Props {
  appBundles: AppBundle[];
  onEditClick: (appBundle: AppBundle) => void;
  onDisableClick: (appBundle: AppBundle) => void;
  isUpdating: boolean;
}

const AppBundlesTable: React.FC<Props> = ({ appBundles, onEditClick, onDisableClick, isUpdating }) => {
  // Fetch available applications for name mapping
  const { data: availableApplications } = useQuery(["listApplications"], {
    queryFn: ApplicationsService.listApplications
  });

  // Get unique application IDs that need full application details for preset names
  const applicationIdsWithPresets = React.useMemo(() => {
    const allApps = appBundles.flatMap(bundle => bundle.applications || []);
    return [...new Set(allApps.filter(app => app.applicationSettingsPresetId).map(app => app.applicationId))];
  }, [appBundles]);

  // Fetch full application details for applications that have presets
  const { data: applicationDetails } = useQuery(
    ["applicationDetails", applicationIdsWithPresets],
    async () => {
      if (applicationIdsWithPresets.length === 0) {
        return {};
      }

      const details: Record<string, Application> = {};
      await Promise.all(
        applicationIdsWithPresets.map(async appId => {
          try {
            const app = await ApplicationsService.getApplication({ id: appId });
            details[appId] = app;
          } catch (error) {
            console.warn(`Failed to fetch application details for ${appId}:`, error);
          }
        })
      );
      return details;
    },
    {
      enabled: applicationIdsWithPresets.length > 0,
      staleTime: 5 * 60 * 1000 // Cache for 5 minutes
    }
  );

  // Helper function to get application name
  const getApplicationName = React.useCallback(
    (applicationId: string) => {
      const app = availableApplications?.items?.find(a => a.id === applicationId);
      return app?.name || applicationId;
    },
    [availableApplications]
  );

  // Helper function to get preset name
  const getPresetName = React.useCallback(
    (applicationId: string, presetId: string) => {
      const appDetails = applicationDetails?.[applicationId];
      if (!appDetails?.settingPresets) {
        return presetId; // Fallback to ID if details not available
      }

      const preset = appDetails.settingPresets.find(p => p.id === presetId);
      return preset?.name || presetId; // Fallback to ID if preset not found
    },
    [applicationDetails]
  );

  // Helper function to format application display text
  const formatApplicationDisplay = React.useCallback(
    (applicationId: string, presetId?: string) => {
      const appName = getApplicationName(applicationId);
      if (presetId) {
        const presetName = getPresetName(applicationId, presetId);
        return `${appName} (${presetName})`;
      }
      return appName;
    },
    [getApplicationName, getPresetName]
  );

  const columns: DataTableColumn[] = [
    {
      id: "name",
      title: "Name",
      cell: (appBundle: AppBundle) => appBundle.name
    },
    {
      id: "status",
      title: "Status",
      cell: (appBundle: AppBundle) => (
        <Chip
          label={appBundle.isDisabled ? "Disabled" : "Active"}
          color={appBundle.isDisabled ? "default" : "success"}
          size="small"
        />
      )
    },
    {
      id: "applications",
      title: "Applications",
      cell: (appBundle: AppBundle) => {
        if (!appBundle.applications || appBundle.applications.length === 0) {
          return (
            <Typography variant="body2" color="text.secondary">
              No applications
            </Typography>
          );
        }

        return (
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
            {appBundle.applications.map((app, index) => (
              <Chip
                key={index}
                label={formatApplicationDisplay(app.applicationId, app.applicationSettingsPresetId)}
                size="small"
                variant="outlined"
                sx={{ mb: 0.5 }}
              />
            ))}
          </Stack>
        );
      }
    }
  ];

  const rowActions = React.useCallback(
    (appBundle: AppBundle): DataTableRowAction[] => [
      {
        title: "Edit",
        action: () => onEditClick(appBundle),
        disabled: isUpdating
      },
      {
        title: appBundle.isDisabled ? "Enable" : "Disable",
        action: () => onDisableClick(appBundle),
        color: appBundle.isDisabled ? "success.main" : "warning.main",
        disabled: isUpdating
      }
    ],
    [onEditClick, onDisableClick, isUpdating]
  );

  const rowKey = React.useCallback((appBundle: AppBundle) => appBundle.id, []);

  return (
    <DataTable
      columns={columns}
      data={appBundles}
      rowKey={rowKey}
      rowActions={rowActions}
      dense={true}
      noData={
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No app bundles have been created yet. Click "Create New App Bundle" to get started.
          </Typography>
        </Box>
      }
    />
  );
};

export { AppBundlesTable };
